import { retryAfterSeconds } from "../../supabase/functions/_shared/openAiHttpError";
import type { LanguageActorSelection } from '../../supabase/functions/_shared/languageActors';
import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { bindPreparedSpeech, clearPreparedSpeech, prepareSpeechAudio, type AuthoredSpeechClip, type PreparedSpeechAudio } from "@/lib/speechAudio";

export type LanguageTTSErrorCode = "authentication" | "account-changed" | "quota"
  | "service-unavailable" | "request-failed" | "invalid-response" | "audio-preparation"
  | "playback-blocked" | "playback-failed";
const MESSAGES: Record<LanguageTTSErrorCode, string> = {
  authentication: "Not authenticated", "account-changed": "Language TTS account changed",
  quota: "Voice allowance unavailable", "service-unavailable": "Voice service unavailable",
  "request-failed": "Voice request failed", "invalid-response": "No audio URL returned",
  "audio-preparation": "Audio preparation failed", "playback-blocked": "Tap to allow audio playback",
  "playback-failed": "Audio playback failed",
};
/** Safe diagnostics: no prompt, user identifier, token or provider response body. */
export class LanguageTTSError extends Error {
  constructor(readonly code: LanguageTTSErrorCode, readonly status?: number, readonly mediaCode?: number, readonly retryAfter?: number) {
    super(MESSAGES[code]); this.name = "LanguageTTSError";
  }
}
export interface LanguageTTSOptions { actor?: LanguageActorSelection }
interface UseLanguageTTS {
  speak: (text: string, lang: string, clip?: AuthoredSpeechClip, options?: LanguageTTSOptions) => Promise<void>;
  /** Call synchronously in a click/tap when speak will be dispatched by an effect. */
  unlockAudio: () => void;
  isLoading: boolean;
  error: LanguageTTSError | null;
  cancel: () => void;
  audioElement: HTMLAudioElement | null;
}

// Self-authored 100 ms silent PCM, used only to bless the persistent native
// player inside the user's gesture. It contains no borrowed audio asset.
function silentWav() {
  const bytes = new Uint8Array(44 + 1600), view = new DataView(bytes.buffer);
  const text = (offset: number, value: string) => [...value].forEach((c, i) => { bytes[offset + i] = c.charCodeAt(0); });
  text(0, "RIFF"); view.setUint32(4, bytes.length - 8, true); text(8, "WAVEfmt ");
  view.setUint32(16, 16, true); view.setUint16(20, 1, true); view.setUint16(22, 1, true);
  view.setUint32(24, 8000, true); view.setUint32(28, 16000, true);
  view.setUint16(32, 2, true); view.setUint16(34, 16, true); text(36, "data"); view.setUint32(40, 1600, true);
  return "data:audio/wav;base64," + btoa(String.fromCharCode(...bytes));
}
function requestError(error: unknown): LanguageTTSError {
  const status = (error as { context?: { status?: unknown } } | null)?.context?.status;
  const http = typeof status === "number" && Number.isInteger(status) ? status : undefined;
  const context = (error as { context?: Response } | null)?.context;
  const retry = retryAfterSeconds(context?.headers?.get?.("Retry-After") ?? null) ?? undefined;
  return new LanguageTTSError(http === 401 || http === 403 ? "authentication"
    : http === 402 ? "quota" : http === 429 || http === 503 ? "service-unavailable" : "request-failed", http, undefined, retry);
}

export function useLanguageTTS(): UseLanguageTTS {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<LanguageTTSError | null>(null);
  const [identityVersion, setIdentityVersion] = useState(0);
  const [audioElement, setAudioElement] = useState<HTMLAudioElement | null>(null);
  const player = useRef<HTMLAudioElement | null>(null);
  const prepared = useRef<PreparedSpeechAudio | null>(null);
  const speaking = useRef(false), unlocked = useRef(false);
  const priming = useRef<Promise<void> | null>(null);
  const mounted = useRef(false), generation = useRef(0), authEpoch = useRef(0);
  const owner = useRef<string | null | undefined>(undefined);
  // Provider availability is shared across users; retain this short pause across account changes.
  // Store a deadline, not a setTimeout delay, so large Retry-After values cannot overflow a timer.
  const retryNotBefore = useRef(0);
  const active = useRef<AbortController | null>(null);

  const stopAudio = useCallback(() => {
    const audio = player.current;
    if (audio) {
      audio.onended = null; audio.onerror = null;
      audio.pause(); clearPreparedSpeech(audio); audio.src = "";
    }
    prepared.current?.release(); prepared.current = null;
    speaking.current = false;
    if (mounted.current) setAudioElement(null);
  }, []);
  const cancel = useCallback(() => {
    generation.current++; active.current?.abort(); active.current = null;
    stopAudio();
    if (mounted.current) setIsLoading(false);
  }, [stopAudio]);

  useEffect(() => {
    mounted.current = true; setIdentityVersion(authEpoch.current);
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!mounted.current) return;
      const next = session?.user.id ?? null;
      if (owner.current === next) return;
      // Initial session discovery is not a user switch. Public authored clips
      // must not be interrupted just because auth finished booting after Play.
      if (owner.current === undefined && _event === "INITIAL_SESSION") { owner.current = next; return; }
      owner.current = next; authEpoch.current++; cancel(); setError(null);
      setIdentityVersion(authEpoch.current);
    });
    const hidden = () => { if (document.hidden) cancel(); };
    document.addEventListener("visibilitychange", hidden);
    window.addEventListener("pagehide", cancel);
    const lifetime = { mounted, authEpoch };
    return () => {
      lifetime.mounted.current = false; lifetime.authEpoch.current++; cancel(); subscription.unsubscribe();
      document.removeEventListener("visibilitychange", hidden);
      window.removeEventListener("pagehide", cancel);
      player.current = null; priming.current = null; unlocked.current = false;
    };
  }, [cancel]);

  const unlockAudio = useCallback(() => {
    if (!mounted.current || identityVersion !== authEpoch.current || unlocked.current || priming.current) return;
    const audio = player.current ?? new Audio();
    player.current = audio;
    audio.setAttribute("playsinline", "");
    audio.setAttribute("webkit-playsinline", "true");
    audio.preload = "auto";
    const primer = silentWav();
    audio.src = primer;
    // play() is deliberately called before ANY await. A late promise from a
    // retired primer must never pause a newer utterance on this same element.
    try {
      const promise = audio.play().then(() => {
        if (mounted.current && player.current === audio) {
          unlocked.current = true;
          if (audio.src === primer && !speaking.current) audio.pause();
        }
      }).catch(() => { /* Actual playback reports a typed error if still blocked. */ });
      priming.current = promise;
      void promise.finally(() => { if (priming.current === promise) priming.current = null; });
    } catch { priming.current = null; }
  }, [identityVersion]);

  const speak = useCallback(async (text: string, lang: string, clip?: AuthoredSpeechClip, options?: LanguageTTSOptions) => {
    if (!mounted.current || identityVersion !== authEpoch.current) return;
    const ticket = ++generation.current, epoch = authEpoch.current;
    active.current?.abort();
    // Preserve an in-flight gesture primer supplied by Story's Play button.
    if (speaking.current || prepared.current) stopAudio();
    unlockAudio();
    const audio = player.current;
    const controller = new AbortController(); active.current = controller;
    const current = () => mounted.current && ticket === generation.current
      && epoch === authEpoch.current && !controller.signal.aborted;
    setError(null); setIsLoading(true);
    let stage: "request" | "prepare" | "play" = "request";
    try {
      let expectedOwner = owner.current;
      let url: string;
      if (clip) {
        // Versioned educational assets are public content, not per-user TTS.
        // Playback neither requires a session nor generates/charges speech.
        if (clip.text !== text || clip.language.toLowerCase() !== lang.toLowerCase()) throw new LanguageTTSError("invalid-response");
        url = clip.url;
      } else {
        if (Date.now() < retryNotBefore.current) {
          const remaining = Math.min(86400, Math.ceil((retryNotBefore.current - Date.now()) / 1000));
          throw new LanguageTTSError("service-unavailable", 503, undefined, remaining);
        }
        const { data: { session }, error: authError } = await supabase.auth.getSession();
        if (!current()) return;
        if (authError || !session?.user.id || !session.access_token) throw new LanguageTTSError("authentication");
        expectedOwner = session.user.id;
        if (owner.current === undefined) owner.current = expectedOwner;
        if (owner.current !== expectedOwner) throw new LanguageTTSError("account-changed");
        const { data, error: fnError } = await supabase.functions.invoke("language-tts", {
          body: { text, lang, ...(options?.actor ? { language_tutor_actor: options.actor } : {}) }, headers: { Authorization: "Bearer " + session.access_token }, signal: controller.signal,
        });
        if (!current() || owner.current !== expectedOwner) return;
        if (fnError) {
          const failure = requestError(fnError);
          if (failure.code === "service-unavailable") {
            retryNotBefore.current = Math.min(Number.MAX_SAFE_INTEGER, Date.now() + Math.max(1, failure.retryAfter ?? 30) * 1000);
          }
          throw failure;
        }
        retryNotBefore.current = 0;
        if (typeof data?.url !== "string" || !data.url) throw new LanguageTTSError("invalid-response");
        url = data.url;
      }
      stage = "prepare";
      const media = await prepareSpeechAudio(url, controller.signal, clip?.mouthCues);
      if (!current() || (!clip && owner.current !== expectedOwner)) { media.release(); return; }
      await priming.current;
      if (!current()) { media.release(); return; }
      if (!audio) { media.release(); throw new LanguageTTSError("playback-failed"); }
      prepared.current = media; audio.src = media.url; audio.currentTime = 0;
      bindPreparedSpeech(audio, media); speaking.current = true;
      setAudioElement(audio);
      audio.onended = () => {
        if (!current() || !speaking.current || prepared.current !== media || audio.src !== media.url || !audio.ended) return;
        stopAudio(); setIsLoading(false);
      };
      audio.onerror = () => {
        if (!current() || !speaking.current || prepared.current !== media || audio.src !== media.url || !audio.error) return;
        const mediaCode = audio.error?.code;
        stopAudio(); setError(new LanguageTTSError("playback-failed", undefined, mediaCode)); setIsLoading(false);
      };
      stage = "play";
      await audio.play();
      if (current()) unlocked.current = true;
      else if (prepared.current === media) audio.pause();
    } catch (cause) {
      if (!current()) return;
      stopAudio();
      const err = cause instanceof LanguageTTSError ? cause
        : new LanguageTTSError(stage === "play"
          ? ((cause as { name?: unknown } | null)?.name === "NotAllowedError" ? "playback-blocked" : "playback-failed")
          : stage === "prepare" ? "audio-preparation" : "request-failed");
      setError(err); setIsLoading(false); throw err;
    } finally {
      if (active.current === controller) active.current = null;
    }
  }, [identityVersion, stopAudio, unlockAudio]);

  return { speak, unlockAudio, isLoading, error, cancel, audioElement };
}
